import {
  DateTimePickerToolbar,
  StaticDateTimePicker,
  TimeField,
} from "@mui/x-date-pickers";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  currentTime,
  fromDayJsToFormatedDate,
  fromDayJsToJsonDate,
  toDayJs,
} from "../../dateConfig";
import "../../css/DateTimePicker.css";
import UIMessage from "../UIMessage";
import useAuthority from "../../hooks/useAuthority";
import Box from "@mui/material/Box";

function CustomToolbar(props) {
  const { currentValue, setCurrentValue } = props;
  return (
    <Box
      className={props.className}
      sx={{
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      <DateTimePickerToolbar
        {...props}
        ampmInClock={false}
        sx={{
          "& .MuiDateTimePickerToolbar-hourMinuteLabel": { display: "none" },
          "& .MuiDateTimePickerToolbar-timeContainer": { display: "none" },
        }}
        toolbarPlaceholder="??"
      />
      <TimeField
        size={"small"}
        value={currentValue}
        onChange={(newValue) => setCurrentValue(newValue)}
        ampm={false}
        minutesStep={1}
        slotProps={{
          textField: {
            inputProps: {
              style: { fontSize: 14, textAlign: "center" },
              placeholder: "hh:mm",
            },
            sx: {
              "& .MuiInputBase-input": {
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: "0.5px",
              },
            },
            fullWidth: true,
          },
        }}
      />
    </Box>
  );
}

function ActionList(props) {
  const { className, customActions } = props;

  return (
    <List
      className={className}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        width: "100%",
      }}
    >
      {customActions.map(({ text, method, className, disabled }) => (
        <ListItem
          key={text}
          style={{
            display: "flex",
            alignItems: "center",
            width: "fit-content",
          }}
          disablePadding
        >
          <button
            type="button"
            disabled={disabled}
            onClick={method}
            className={className}
          >
            {text}
          </button>
        </ListItem>
      ))}
    </List>
  );
}

// Send the value in JSON Date format
// By default this returns a JSON date
// if returnJsonDate = true this returns a date of type DATE_FORMAT in dateConfig.js
/*
  validators is an array of objects with the following structure
  {
    func: (date) => boolean,
    message: "Error message"
  }
  func is a function that takes a date and returns a boolean
  ** A list of functions with the necessary conversion can be found in dateConfig.js
     only use these functions or add more functions with necessary conversions to dateConfig.js
  message is the error message to be displayed if the function returns false

  Example

  validators={[
            {
              func: (date) => isAfter(date, new Date()),
              message: "Date should be after current date",
            },
  ]}
*/

const PopUpDateTimePicker = ({
  value,
  onAccept,
  onCancel,
  id,
  returnJsonDate = true,
  validators = [],
  field = "field",
  module = "module",
  form = "form",
  authenticated = false,
  editMode = false,
  disabled = false,
  minDate,
  minDateTime,
  maxDateTime,
}) => {
  const { view, authorized } = useAuthority({
    module,
    form,
    field,
    editMode,
    authenticated,
  });

  const [currentValue, setCurrentValue] = useState(
    value ? toDayJs(value) : currentTime,
  );
  const [error, setError] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);

  const validate = useCallback(
    (date) => {
      if (!validators || validators.length === 0) return true;
      for (let validator of validators) {
        if (!validator.func(date)) {
          setError(validator.message);
          setSubmitDisabled(true);
          return false;
        }
      }
      setError(null);
      setSubmitDisabled(false);
      return true;
    },
    [validators],
  );

  useEffect(() => {
    validate(currentValue);
  }, [currentValue, validate]);

  const customActions = useMemo(
    () => [
      {
        text: "Cancel",
        method: onCancel,
        className: "ui button",
        disabled: false,
      },
      {
        main: true,
        text: "Submit",
        method: () => {
          onAccept(
            returnJsonDate
              ? fromDayJsToJsonDate(currentValue)
              : fromDayJsToFormatedDate(currentValue),
          );
        },
        className: "ui primary button",
        disabled: submitDisabled,
      },
    ],
    [onCancel, onAccept, returnJsonDate, currentValue, submitDisabled],
  );

  return (
    <>
      <StaticDateTimePicker
        style={view ? {} : { display: "none" }}
        key={id}
        ampm={false}
        disabled={disabled || !authorized}
        className="date-time-picker"
        orientation="landscape"
        value={currentValue}
        minDate={minDate ? toDayJs(minDate) : undefined}
        minDateTime={minDateTime ? toDayJs(minDateTime) : undefined}
        maxDateTime={maxDateTime ? toDayJs(maxDateTime) : undefined}
        onChange={(value) => {
          console.log("VALUE : " + value);
          if (validate(value)) {
            setCurrentValue(value);
          }
        }}
        ampmInClock={true}
        onAccept={() => {
          console.log("VALUE : ", currentValue);
          console.log("JSON VALUE : ", fromDayJsToJsonDate(currentValue));
          onAccept(
            returnJsonDate
              ? fromDayJsToJsonDate(currentValue)
              : fromDayJsToFormatedDate(currentValue),
          );
        }}
        slots={{
          actionBar: ActionList,
          toolbar: CustomToolbar,
        }}
        slotProps={{
          actionBar: {
            customActions,
          },
          toolbar: {
            currentValue,
            setCurrentValue,
          },
        }}
      />
      {error && (
        <UIMessage width="95%" type="negative">
          {error}
        </UIMessage>
      )}
    </>
  );
};

export default PopUpDateTimePicker;
